import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Search, Sun, Moon, User, LogOut, Crown, ChevronDown, Sparkles, UserPlus, Info, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useEntitlements } from '@/hooks/useEntitlements';
import { cn } from '@/lib/utils';
import { setSubscriptionIntent } from '@/lib/subscriptionIntent';

// Industry Insights price ID
const INDUSTRY_INSIGHTS_PRICE_ID = 'price_1SvOYeL8vTfzejZspOgPRszT';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { PrefetchLink } from '@/components/shared/PrefetchLink';

// Navigation links - Industry Insights has a dropdown
const navLinks = [
  { href: '/', label: 'Home' },
  { href: '/news', label: 'Latest News' },
  { href: '/reviews', label: 'Reviews' },
  { href: '/deals/builder', label: 'Deal IQ' },
  { href: '/events', label: 'Events' },
];

// Industry Insights dropdown menu items
const insightsMenuItems = [
  { href: '/insights/dashboard', label: 'Industry Insider', icon: Crown, requiresSubscription: true },
  { href: '/insights', label: 'Sign Up', icon: UserPlus, requiresSubscription: false },
];

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const { user, isDealer, signOut } = useAuth();
  const { isSubscribed } = useEntitlements();
  const location = useLocation();
  const navigate = useNavigate();

  // Determine the correct account URL based on user type
  const accountUrl = isDealer ? '/dealer/account' : '/account';

  // Handle subscription sign up - sets intent and navigates to auth
  const handleSubscriptionSignUp = () => {
    setSubscriptionIntent({
      product: 'industry_insights',
      priceId: INDUSTRY_INSIGHTS_PRICE_ID,
      returnTo: '/insights/dashboard',
    });
    navigate('/auth/buyer');
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <span className="font-display text-2xl font-bold text-primary">
              Auto News Weekly
            </span>
          </Link>

          {/* Desktop Navigation - with prefetching */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <PrefetchLink
                key={link.href}
                to={link.href}
                className={cn(
                  "px-3 py-2 text-sm font-medium rounded-md transition-colors",
                  location.pathname === link.href
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                )}
              >
                {link.label}
              </PrefetchLink>
            ))}
            
            {/* Industry Insights Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={cn(
                    "px-3 py-2 text-sm font-medium rounded-md transition-colors inline-flex items-center gap-1",
                    location.pathname.startsWith('/insights')
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  )}
                >
                  <Sparkles className="h-4 w-4" />
                  Industry Insights
                  <ChevronDown className="h-3 w-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48 bg-popover z-50">
                <DropdownMenuItem asChild>
                  <Link to="/insights" className="cursor-pointer flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    About
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/insights/dashboard" className="cursor-pointer flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Insights Hub
                  </Link>
                </DropdownMenuItem>
                {!isSubscribed && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={handleSubscriptionSignUp}
                      className="cursor-pointer flex items-center gap-2"
                    >
                      <UserPlus className="h-4 w-4" />
                      Sign Up
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Right side actions */}
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              aria-label="Search"
            >
              <Search className="h-5 w-5" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label="Toggle theme"
            >
            {theme === 'light' ? (
                <Moon className="h-5 w-5" />
              ) : (
                <Sun className="h-5 w-5" />
              )}
            </Button>

            {/* Notification Bell (for buyers only) */}
            <NotificationBell />

            {/* Auth button */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Account menu">
                    <User className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {isDealer ? (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/dealer/dashboard" className="cursor-pointer">
                          Dashboard
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/dealer/account" className="cursor-pointer">
                          My Account
                        </Link>
                      </DropdownMenuItem>
                    </>
                  ) : (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/account" className="cursor-pointer">
                          Dashboard
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/account" className="cursor-pointer">
                          My Account
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => signOut()} className="cursor-pointer">
                    <LogOut className="h-4 w-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button asChild variant="default" size="sm" className="hidden sm:flex">
                <Link to="/auth/buyer">Login / Sign Up</Link>
              </Button>
            )}

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>

        {/* Search bar */}
        {isSearchOpen && (
          <div className="py-4 border-t border-border animate-fade-in">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="search"
                placeholder="Search articles, reviews, events..."
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                autoFocus
              />
            </div>
          </div>
        )}

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="lg:hidden py-4 border-t border-border animate-fade-in">
            <div className="flex flex-col space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsMenuOpen(false)}
                  className={cn(
                    "px-3 py-2 text-sm font-medium rounded-md transition-colors",
                    location.pathname === link.href
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  )}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Industry Insights mobile menu items */}
              <div className="border-t border-border pt-2 mt-1">
                <p className="px-3 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
                  <Sparkles className="h-3 w-3" />
                  Industry Insights
                </p>
                <Link
                  to="/insights"
                  onClick={() => setIsMenuOpen(false)}
                  className="px-3 py-2 text-sm font-medium rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-2"
                >
                  <Info className="h-4 w-4" />
                  About
                </Link>
                <Link
                  to="/insights/dashboard"
                  onClick={() => setIsMenuOpen(false)}
                  className="px-3 py-2 text-sm font-medium rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-2"
                >
                  <FileText className="h-4 w-4" />
                  Insights Hub
                </Link>
                {!isSubscribed && (
                  <button
                    onClick={() => {
                      setIsMenuOpen(false);
                      handleSubscriptionSignUp();
                    }}
                    className="px-3 py-2 text-sm font-medium rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-2 w-full text-left"
                  >
                    <UserPlus className="h-4 w-4" />
                    Sign Up
                  </button>
                )}
              </div>
              
              {/* Mobile auth link */}
              {user ? (
                <>
                  <Link
                    to={accountUrl}
                    onClick={() => setIsMenuOpen(false)}
                    className="px-3 py-2 text-sm font-medium rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-muted"
                  >
                    My Account
                  </Link>
                  {isDealer && (
                    <Link
                      to="/dealer/dashboard"
                      onClick={() => setIsMenuOpen(false)}
                      className="px-3 py-2 text-sm font-medium rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-muted"
                    >
                      Dealer Dashboard
                    </Link>
                  )}
                  <button
                    onClick={() => {
                      signOut();
                      setIsMenuOpen(false);
                    }}
                    className="px-3 py-2 text-sm font-medium rounded-md transition-colors text-muted-foreground hover:text-foreground hover:bg-muted text-left"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <Link
                  to="/auth/buyer"
                  onClick={() => setIsMenuOpen(false)}
                  className="px-3 py-2 text-sm font-medium rounded-md transition-colors bg-primary text-primary-foreground"
                >
                  Login / Sign Up
                </Link>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}